import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "react-oidc-context";
import { FiEdit2, FiCheck, FiX, FiSearch, FiMapPin, FiChevronRight } from "react-icons/fi";
import { updateSite } from "../utils/api";
import {
  Card,
  Input,
  Select,
  Badge,
  Mono,
  IconButton,
  Button,
  GhostButton,
  EmptyState,
  Skeleton,
  Row,
} from "./ui";
import { formatCurrency, formatDate, currencyCode } from "../utils/format";

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space(3)};
  margin-bottom: ${({ theme }) => theme.space(4)};
  flex-wrap: wrap;
`;

const Filters = styled.div`
  display: inline-flex;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 4px;
  gap: 2px;
`;

const FilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ active, theme }) => (active ? theme.colors.accentSoft : "transparent")};
  color: ${({ active, theme }) => (active ? theme.colors.accent : theme.colors.textMuted)};
  border: 0;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: background 0.15s ease, color 0.15s ease;
  &:hover { background: ${({ theme }) => theme.colors.bgAlt}; }
`;

const SearchWrap = styled.div`
  position: relative;
  width: 320px;
  max-width: 100%;
  svg {
    position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
    width: 16px; height: 16px;
    color: ${({ theme }) => theme.colors.textMuted};
  }
  input { padding-left: 36px; }
`;

const Wrap = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th, td {
    padding: 12px 16px;
    text-align: left;
    vertical-align: middle;
  }

  thead th {
    background: ${({ theme }) => theme.colors.bgAlt};
    color: ${({ theme }) => theme.colors.textMuted};
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  tbody tr {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    transition: background 0.12s ease;
    cursor: pointer;
  }
  tbody tr:last-child { border-bottom: 0; }
  tbody tr:hover { background: ${({ theme }) => theme.colors.surfaceHover}; }

  ${({ theme }) => theme.media.lg} {
    display: none;
  }
`;

const CardList = styled.div`
  display: none;
  ${({ theme }) => theme.media.lg} {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.space(3)};
    padding: ${({ theme }) => theme.space(3)};
  }
`;

const SiteCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.space(4)};
  cursor: pointer;
  transition: border-color 0.15s ease;
  &:hover { border-color: ${({ theme }) => theme.colors.accent}; }

  .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 10px; }
  .name { font-weight: 600; font-size: 15px; }
  .addr { display: flex; gap: 6px; align-items: flex-start; color: ${({ theme }) => theme.colors.textMuted}; font-size: 13px; }
  .addr svg { flex: 0 0 14px; width: 14px; height: 14px; margin-top: 3px; }

  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 13px; margin-top: 12px; }
  .k { color: ${({ theme }) => theme.colors.textMuted}; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 2px; }
  .foot { display: flex; justify-content: space-between; align-items: center; margin-top: 12px; color: ${({ theme }) => theme.colors.accent}; font-size: 13px; font-weight: 600; }
`;

const SitesTable = ({ sites, loading, onSiteUpdated }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [edited, setEdited] = useState({});

  const baseFiltered = sites.filter((site) => {
    const q = search.toLowerCase();
    return (
      (site.name || "").toLowerCase().includes(q) ||
      (site.address || "").toLowerCase().includes(q)
    );
  });

  const filtered = statusFilter === "all"
    ? baseFiltered
    : baseFiltered.filter((s) => (statusFilter === "active" ? s.status === 1 : s.status !== 1));

  const totals = {
    all: baseFiltered.length,
    active: baseFiltered.filter((s) => s.status === 1).length,
    inactive: baseFiltered.filter((s) => s.status !== 1).length,
  };

  const beginEdit = (e, site) => {
    e.stopPropagation();
    setEditingId(site.site_id);
    setEdited({
      name: site.name,
      lat: site.lat,
      lon: site.lon,
      instruction: site.instruction || "",
      offering: site.offering || "CASUAL",
      short_term_price: site.short_term_price ?? "",
      long_term_price: site.long_term_price ?? "",
      status: site.status,
    });
  };

  const cancelEdit = (e) => {
    e?.stopPropagation();
    setEditingId(null);
    setEdited({});
  };

  const save = async (e, siteId) => {
    e?.stopPropagation();
    try {
      await updateSite(auth.user?.id_token, { site_id: siteId, ...edited });
      cancelEdit();
      onSiteUpdated?.();
    } catch (err) {
      console.error("Failed to update site:", err);
    }
  };

  const goToSite = (site) => {
    if (editingId) return;
    navigate(`/site/${site.site_id}`, { state: { siteName: site.name } });
  };

  return (
    <>
      <Toolbar>
        <Filters>
          <FilterChip active={statusFilter === "all"} onClick={() => setStatusFilter("all")}>All {totals.all}</FilterChip>
          <FilterChip active={statusFilter === "active"} onClick={() => setStatusFilter("active")}>Active {totals.active}</FilterChip>
          <FilterChip active={statusFilter === "inactive"} onClick={() => setStatusFilter("inactive")}>Inactive {totals.inactive}</FilterChip>
        </Filters>
        <SearchWrap>
          <FiSearch />
          <Input
            type="search"
            placeholder="Search by name or address…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchWrap>
      </Toolbar>

      <Wrap>
        {loading ? (
          <div style={{ padding: 24 }}>
            <Skeleton h={20} /><div style={{ height: 12 }} />
            <Skeleton h={20} /><div style={{ height: 12 }} />
            <Skeleton h={20} />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState>
            <h3>No sites match</h3>
            <p>{search ? "Try a different search or clear filters." : "Add your first site to get started."}</p>
          </EmptyState>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Address</th>
                  <th>Coordinates</th>
                  <th>Added</th>
                  <th>Offering</th>
                  <th>Short-term ({currencyCode}/hr)</th>
                  <th>Long-term ({currencyCode}/wk)</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((site) => {
                  const isEditing = editingId === site.site_id;
                  return (
                    <tr key={site.site_id} onClick={() => goToSite(site)}>
                      <td>
                        {isEditing ? (
                          <Input
                            value={edited.name}
                            onChange={(e) => setEdited((p) => ({ ...p, name: e.target.value }))}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <strong>{site.name}</strong>
                        )}
                      </td>
                      <td style={{ color: "#6B6864", maxWidth: 260 }}>{site.address}</td>
                      <td>
                        {isEditing ? (
                          <Row gap={1} onClick={(e) => e.stopPropagation()}>
                            <Input type="number" step="0.000001" value={edited.lat} onChange={(e) => setEdited((p) => ({ ...p, lat: e.target.value }))} style={{ width: 110 }} />
                            <Input type="number" step="0.000001" value={edited.lon} onChange={(e) => setEdited((p) => ({ ...p, lon: e.target.value }))} style={{ width: 110 }} />
                          </Row>
                        ) : (
                          <Mono>{`${site.lat}, ${site.lon}`}</Mono>
                        )}
                      </td>
                      <td style={{ color: "#6B6864" }}>{formatDate(site.date_added)}</td>
                      <td>
                        {isEditing ? (
                          <Select value={edited.offering} onChange={(e) => setEdited((p) => ({ ...p, offering: e.target.value }))} onClick={(e) => e.stopPropagation()}>
                            <option value="CASUAL">Casual</option>
                            <option value="LONG_TERM">Long term</option>
                            <option value="BOTH">Both</option>
                          </Select>
                        ) : (
                          <Badge tone="neutral">{site.offering || "—"}</Badge>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <Input type="number" step="0.01" value={edited.short_term_price} onChange={(e) => setEdited((p) => ({ ...p, short_term_price: e.target.value }))} onClick={(e) => e.stopPropagation()} style={{ width: 110 }} />
                        ) : (
                          formatCurrency(site.short_term_price)
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <Input type="number" step="0.01" value={edited.long_term_price} onChange={(e) => setEdited((p) => ({ ...p, long_term_price: e.target.value }))} onClick={(e) => e.stopPropagation()} style={{ width: 110 }} />
                        ) : (
                          formatCurrency(site.long_term_price)
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <Select value={edited.status ?? site.status ?? 0} onChange={(e) => setEdited((p) => ({ ...p, status: parseInt(e.target.value, 10) }))} onClick={(e) => e.stopPropagation()}>
                            <option value={1}>Active</option>
                            <option value={0}>Inactive</option>
                          </Select>
                        ) : (
                          <Badge tone={site.status === 1 ? "success" : "danger"}>
                            {site.status === 1 ? "Active" : "Inactive"}
                          </Badge>
                        )}
                      </td>
                      <td>
                        <Row justify="flex-end" gap={1}>
                          {isEditing ? (
                            <>
                              <Button onClick={(e) => save(e, site.site_id)} style={{ padding: "6px 12px" }}>
                                <FiCheck /> Save
                              </Button>
                              <GhostButton onClick={cancelEdit}>
                                <FiX /> Cancel
                              </GhostButton>
                            </>
                          ) : (
                            <IconButton onClick={(e) => beginEdit(e, site)} aria-label="Edit site" title="Edit">
                              <FiEdit2 />
                            </IconButton>
                          )}
                        </Row>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            <CardList>
              {filtered.map((site) => (
                <SiteCard key={site.site_id} onClick={() => goToSite(site)}>
                  <div className="head">
                    <div>
                      <div className="name">{site.name}</div>
                      <div className="addr"><FiMapPin /> {site.address}</div>
                    </div>
                    <Badge tone={site.status === 1 ? "success" : "danger"}>
                      {site.status === 1 ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <div className="grid">
                    <div><div className="k">Offering</div>{site.offering || "—"}</div>
                    <div><div className="k">Added</div>{formatDate(site.date_added)}</div>
                    <div><div className="k">Short-term</div>{formatCurrency(site.short_term_price)} <span style={{ color: "#8E8B86" }}>/hr</span></div>
                    <div><div className="k">Long-term</div>{formatCurrency(site.long_term_price)} <span style={{ color: "#8E8B86" }}>/wk</span></div>
                  </div>
                  <div className="foot">
                    <span>Manage bays</span>
                    <FiChevronRight />
                  </div>
                </SiteCard>
              ))}
            </CardList>
          </>
        )}
      </Wrap>
    </>
  );
};

export default SitesTable;
